import { useNavigate } from "react-router-dom";
import { MapPin, Phone, Globe, ShieldCheck, Hand, Sparkles } from "lucide-react";

function fmtPhone(p) {
  if (!p) return "";
  const d = p.replace(/[^0-9]/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === "1") return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p;
}
const stop = (e) => e.stopPropagation();

export default function ListingCard({ listing }) {
  const navigate = useNavigate();
  // Photo policy: only owner-uploaded hero_url is shown. No stock fallback.
  const heroImg = listing.hero_url || null;
  const isPerson = Boolean(listing.first_name || listing.last_name);
  const personName = [listing.first_name, listing.last_name].filter(Boolean).join(" ");
  const displayName = isPerson ? personName : (listing.practice_name || "");
  const subline = isPerson
    ? (listing.specialty || listing.practice_name || "")
    : (listing.specialty || "");

  const isVerified = listing.is_verified === true;
  const isUnclaimed = listing.status === "unclaimed";
  const isSponsored = listing.is_sponsored === true;
  const websiteUrl = listing.website
    ? listing.website.startsWith("http") ? listing.website : `https://${listing.website}`
    : null;
  const goDetail = () => navigate(`/listing/${listing.id}`);
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goDetail(); }
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={goDetail}
      onKeyDown={onKey}
      className="card-surface card-hover group block cursor-pointer overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      data-testid={`listing-card-${listing.id}`}
    >
      {heroImg && (
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={heroImg}
            alt={displayName}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            loading="lazy"
            decoding="async"
          />
          <span className="chip absolute left-3 top-3 bg-white/90">
            {listing.category}
          </span>
          {isSponsored && (
            <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-accent-foreground shadow-[0_2px_8px_rgba(217,139,40,0.45)]" data-testid={`badge-sponsored-${listing.id}`}>
              <Sparkles className="h-3 w-3" /> Sponsored
            </span>
          )}
          {!isSponsored && isVerified && (
            <span className="badge-verified absolute right-3 top-3" data-testid={`badge-verified-${listing.id}`}>
              <ShieldCheck className="h-3 w-3" /> Verified
            </span>
          )}
          {!isSponsored && !isVerified && isUnclaimed && (
            <span className="badge-unclaimed absolute right-3 top-3" data-testid={`badge-unclaimed-${listing.id}`}>
              <Hand className="h-3 w-3" /> Claim this listing
            </span>
          )}
        </div>
      )}
      <div className="space-y-2 p-5">
        {!heroImg && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="chip bg-primary/8 text-primary">{listing.category}</span>
            {isSponsored && (
              <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-foreground" data-testid={`badge-sponsored-${listing.id}`}>
                <Sparkles className="h-3 w-3" /> Sponsored
              </span>
            )}
            {!isSponsored && isVerified && (
              <span className="badge-verified" data-testid={`badge-verified-${listing.id}`}>
                <ShieldCheck className="h-3 w-3" /> Verified
              </span>
            )}
            {!isSponsored && !isVerified && isUnclaimed && (
              <span className="badge-unclaimed" data-testid={`badge-unclaimed-${listing.id}`}>
                <Hand className="h-3 w-3" /> Claim this listing
              </span>
            )}
          </div>
        )}
        <h3 className="font-display text-2xl font-bold leading-tight text-primary line-clamp-2" data-testid={`listing-name-${listing.id}`}>
          {displayName}
        </h3>
        {subline && (
          <p className="text-sm font-medium text-muted-foreground line-clamp-2" data-testid={`listing-subline-${listing.id}`}>
            {subline}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-2 text-sm text-foreground/80">
          {(listing.city || listing.state) && (
            <span className="inline-flex items-center gap-1" data-testid={`card-location-${listing.id}`}>
              <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              {[listing.city, listing.state].filter(Boolean).join(", ")}
            </span>
          )}
          {listing.phone && (
            <>
              <span className="text-muted-foreground">·</span>
              <a
                href={`tel:${listing.phone.replace(/[^0-9+]/g, "")}`}
                onClick={stop}
                className="inline-flex items-center gap-1 font-semibold text-primary hover:text-accent"
                data-testid={`card-phone-${listing.id}`}
              >
                <Phone className="h-3.5 w-3.5 shrink-0" />
                {fmtPhone(listing.phone)}
              </a>
            </>
          )}
          {websiteUrl && (
            <>
              <span className="text-muted-foreground">·</span>
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={stop}
                className="inline-flex items-center gap-1 font-semibold text-primary hover:text-accent"
                data-testid={`card-website-${listing.id}`}
              >
                <Globe className="h-3.5 w-3.5 shrink-0" />
                Website
              </a>
            </>
          )}
        </div>
        {(listing.accepts_medicaid || listing.accepts_medicare) && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {listing.accepts_medicaid && <span className="rounded-md bg-accent/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-dark" data-testid={`chip-medicaid-${listing.id}`}>Medicaid</span>}
            {listing.accepts_medicare && <span className="rounded-md bg-accent/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-dark" data-testid={`chip-medicare-${listing.id}`}>Medicare</span>}
          </div>
        )}
      </div>
    </div>
  );
}
